import { Button, Card, Muted, Stack, color, radius, space, status } from '@biopeak/ui';
import { useEffect, useRef, useState } from 'react';
import {
  useChatMessages,
  useChatRealtime,
  useFeedMessages,
  useMarkChatRead,
  useSendChatMessage,
  useSendFeedMessage,
  type ChatMessage,
  type FeedMessage,
} from '../lib/communication.ts';
import { formatElapsed } from '../lib/format.ts';

/**
 * Aba "Comunicação" — composer de comunicados + chat (§4.2, §3.2).
 *
 * Os dois canais aparecem na mesma aba mas em cards separados, e a separação é
 * deliberada: são canais com contratos diferentes. O comunicado é mão única,
 * carrega orientação formal e tem recibo de leitura; o chat é conversa. Fundir
 * os dois numa caixa só faria o médico perder a única superfície onde ele
 * consegue saber se a orientação chegou.
 */
export function PatientCommunication({ patientId }: { patientId: string }) {
  return (
    <Stack gap={space.lg}>
      <FeedComposer patientId={patientId} />
      <ChatPanel patientId={patientId} />
    </Stack>
  );
}

// ------------------------------------------------------------ comunicados

function FeedComposer({ patientId }: { patientId: string }) {
  const [body, setBody] = useState('');
  const messages = useFeedMessages(patientId);
  const send = useSendFeedMessage(patientId);

  const tooShort = body.trim().length === 0;

  return (
    <Card>
      <Stack gap={space.md}>
        <Stack gap={space.xs}>
          <h2 style={{ margin: 0, fontSize: '19px' }}>Comunicados</h2>
          <Muted style={{ fontSize: '13px' }}>
            Mão única: o paciente lê e pode confirmar que entendeu, mas não responde aqui. Para
            conversa, use o chat abaixo.
          </Muted>
        </Stack>

        <Stack gap={space.xs}>
          <label htmlFor="feed-body" style={{ fontSize: '14px', fontWeight: 500 }}>
            Novo comunicado
          </label>
          <textarea
            id="feed-body"
            value={body}
            onChange={(e) => setBody(e.target.value)}
            rows={3}
            placeholder="Orientação, lembrete ou recado para este paciente."
            style={{
              border: `1px solid ${color.border}`,
              borderRadius: radius.input,
              padding: '10px 12px',
              fontSize: '15px',
              fontFamily: 'inherit',
              resize: 'vertical',
            }}
          />
        </Stack>

        {send.isError && (
          <Muted style={{ color: status.red.fg, fontSize: '13px' }}>
            {(send.error as Error).message}
          </Muted>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            disabled={tooShort || send.isPending}
            onClick={() =>
              send.mutate(body, { onSuccess: () => setBody('') })
            }
            style={{ fontSize: '15px', padding: '10px 20px' }}
          >
            {send.isPending ? 'Enviando…' : 'Enviar comunicado'}
          </Button>
        </div>

        <div style={{ borderTop: `1px solid ${color.border}`, paddingTop: space.md }}>
          {messages.isLoading && <Muted>Carregando…</Muted>}
          {messages.data?.length === 0 && (
            <Muted style={{ fontSize: '13px' }}>Nenhum comunicado enviado ainda.</Muted>
          )}
          <Stack gap={space.md}>
            {(messages.data ?? []).map((m) => (
              <FeedItem key={m.id} message={m} />
            ))}
          </Stack>
        </div>
      </Stack>
    </Card>
  );
}

/**
 * Um comunicado e o seu estado.
 *
 * Três estados, não dois: enviado → lido → confirmado. "Abriu" e "disse que
 * entendeu" são fatos diferentes, e o schema os guarda em colunas diferentes
 * justamente porque o médico precisa distinguir um do outro. Colapsá-los na UI
 * jogaria fora a informação que a confirmação existe para dar.
 */
function FeedItem({ message }: { message: FeedMessage }) {
  let estado: string;
  let cor: string = color.textSecondary;

  if (message.acknowledged_at) {
    estado = `confirmado ${formatElapsed(message.acknowledged_at)}`;
    cor = status.green.fg;
  } else if (message.read_at) {
    estado = `lido ${formatElapsed(message.read_at)}`;
  } else {
    estado = 'ainda não lido';
  }

  return (
    <Stack gap={space.xs}>
      <div style={{ display: 'flex', gap: space.sm, alignItems: 'baseline', flexWrap: 'wrap' }}>
        <Muted style={{ fontSize: '12px' }}>enviado {formatElapsed(message.sent_at)}</Muted>
        <span style={{ fontSize: '12px', color: cor }}>· {estado}</span>
      </div>
      <span style={{ fontSize: '15px', whiteSpace: 'pre-wrap' }}>{message.body}</span>
    </Stack>
  );
}

// ------------------------------------------------------------------- chat

function ChatPanel({ patientId }: { patientId: string }) {
  const [body, setBody] = useState('');
  const messages = useChatMessages(patientId);
  const send = useSendChatMessage(patientId);
  const markRead = useMarkChatRead(patientId);
  useChatRealtime(patientId);

  const fim = useRef<HTMLDivElement>(null);
  const jaMarcou = useRef(false);

  // Abrir a aba é o ato de ler. Uma vez por montagem: sem a trava, cada
  // chegada de mensagem pelo Realtime dispararia a marcação de novo, e o
  // `useEffect` reagiria à própria invalidação num laço.
  useEffect(() => {
    if (jaMarcou.current) return;
    if (!messages.data?.some((m) => m.sender_role === 'patient' && !m.read_at)) return;
    jaMarcou.current = true;
    markRead.mutate();
  }, [messages.data, markRead]);

  useEffect(() => {
    fim.current?.scrollIntoView({ block: 'nearest' });
  }, [messages.data]);

  const tooShort = body.trim().length === 0;

  return (
    <Card>
      <Stack gap={space.md}>
        <Stack gap={space.xs}>
          <h2 style={{ margin: 0, fontSize: '19px' }}>Chat</h2>
          <Muted style={{ fontSize: '13px' }}>
            Conversa livre, nos dois sentidos. Fica registrada aqui — diferente do WhatsApp, onde
            só o fato de você ter aberto o contato é auditado.
          </Muted>
        </Stack>

        <div
          style={{
            maxHeight: '380px',
            overflowY: 'auto',
            display: 'flex',
            flexDirection: 'column',
            gap: space.sm,
            paddingRight: space.xs,
          }}
        >
          {messages.isLoading && <Muted>Carregando…</Muted>}
          {messages.data?.length === 0 && (
            <Muted style={{ fontSize: '13px' }}>Nenhuma mensagem ainda.</Muted>
          )}
          {(messages.data ?? []).map((m) => (
            <ChatBubble key={m.id} message={m} />
          ))}
          <div ref={fim} />
        </div>

        {send.isError && (
          <Muted style={{ color: status.red.fg, fontSize: '13px' }}>
            {(send.error as Error).message}
          </Muted>
        )}

        <div style={{ display: 'flex', gap: space.sm, alignItems: 'flex-end' }}>
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            rows={2}
            placeholder="Escreva uma mensagem…"
            aria-label="Mensagem para o paciente"
            style={{
              flex: 1,
              border: `1px solid ${color.border}`,
              borderRadius: radius.input,
              padding: '10px 12px',
              fontSize: '15px',
              fontFamily: 'inherit',
              resize: 'vertical',
            }}
          />
          <Button
            disabled={tooShort || send.isPending}
            onClick={() => send.mutate(body, { onSuccess: () => setBody('') })}
            style={{ fontSize: '15px', padding: '10px 20px' }}
          >
            {send.isPending ? 'Enviando…' : 'Enviar'}
          </Button>
        </div>
      </Stack>
    </Card>
  );
}

function ChatBubble({ message }: { message: ChatMessage }) {
  const doMedico = message.sender_role === 'doctor';

  return (
    <div style={{ display: 'flex', justifyContent: doMedico ? 'flex-end' : 'flex-start' }}>
      <Stack gap={space.xs} style={{ maxWidth: '72%', alignItems: doMedico ? 'flex-end' : 'flex-start' }}>
        <div
          style={{
            background: doMedico ? `${color.accent}12` : color.surface,
            border: `1px solid ${doMedico ? `${color.accent}33` : color.border}`,
            borderRadius: radius.card,
            padding: '10px 14px',
            fontSize: '15px',
            whiteSpace: 'pre-wrap',
          }}
        >
          {message.body}
        </div>
        <Muted style={{ fontSize: '11px' }}>
          {formatElapsed(message.sent_at)}
          {/* "lida" só nas do médico: é recibo do paciente. Nas mensagens do
              paciente o recibo seria a própria leitura de quem está olhando a
              tela agora, o que não informa nada. */}
          {doMedico && message.read_at && ' · lida'}
        </Muted>
      </Stack>
    </div>
  );
}
